import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SiteProofLogo from '../../components/SiteProofLogo';

const Section = ({ number, title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold text-white mb-3">
      {number}. {title}
    </h2>
    <div className="space-y-3 text-sm text-slate-400 leading-relaxed">{children}</div>
  </section>
);

const DataProcessingAgreement = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-3 mb-6">
            <Link to="/" className="no-underline">
              <SiteProofLogo size="sm" />
            </Link>
          </div>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to SiteProof
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mt-2">
            Data Processing Agreement
          </h1>
          <p className="text-sm text-gray-500 mt-2">Last updated: 1 March 2026</p>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Intro box */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
          <p className="text-sm text-gray-600">
            This Data Processing Agreement ("<strong>DPA</strong>") forms part of the agreement between{' '}
            <strong>SiteProof Ltd</strong> (Company number: 17085500, registered in England and Wales,
            "<strong>Processor</strong>") and the customer entity that has agreed to the SiteProof Terms and
            Conditions ("<strong>Controller</strong>"). This DPA governs the processing of personal data by
            SiteProof Ltd on behalf of the Controller in connection with the SiteProof platform.
          </p>
        </div>

        <div className="overflow-y-auto">
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">1. Definitions</h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>In this DPA, the following terms have the meanings set out below:</p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li><strong>"Personal Data"</strong> means any information relating to an identified or identifiable natural person processed under this DPA.</li>
                <li><strong>"Processing"</strong> means any operation performed on Personal Data, including collection, storage, use, disclosure, or deletion.</li>
                <li><strong>"Data Subject"</strong> means the individual to whom Personal Data relates.</li>
                <li><strong>"Sub-processor"</strong> means any third party engaged by SiteProof Ltd to process Personal Data on behalf of the Controller.</li>
                <li><strong>"UK GDPR"</strong> means the UK General Data Protection Regulation as retained in UK law by the European Union (Withdrawal) Act 2018.</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">2. Scope and Roles</h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>
                The Controller determines the purposes and means of processing Personal Data uploaded to the
                SiteProof platform. SiteProof Ltd acts as a Processor, processing Personal Data only on the
                documented instructions of the Controller.
              </p>
              <p>
                This DPA applies to all Personal Data processed by SiteProof Ltd in connection with the
                provision of the SiteProof platform, including worker profiles, site records, compliance
                documents, and any other data uploaded by the Controller.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">3. Processor Obligations</h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>SiteProof Ltd shall:</p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>Process Personal Data only on documented instructions from the Controller, unless required to do so by applicable law</li>
                <li>Ensure that persons authorised to process Personal Data are bound by appropriate confidentiality obligations</li>
                <li>Implement appropriate technical and organisational security measures as described in Section 5</li>
                <li>Not engage Sub-processors without prior written authorisation from the Controller, except as set out in Section 6</li>
                <li>Assist the Controller in responding to Data Subject requests and in meeting obligations under UK GDPR</li>
                <li>Delete or return all Personal Data to the Controller upon termination of the service, as described in Section 7</li>
                <li>Make available all information necessary to demonstrate compliance with this DPA</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">4. Controller Obligations</h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>The Controller warrants and represents that:</p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>It has a lawful basis under UK GDPR for processing the Personal Data it uploads to SiteProof</li>
                <li>It has provided all required notices to, and obtained all required consents from, Data Subjects whose data is uploaded to the platform</li>
                <li>Its instructions to SiteProof Ltd comply with applicable data protection law</li>
                <li>It is responsible for the accuracy and legality of Personal Data uploaded to the platform</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">5. Security Measures</h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>
                SiteProof Ltd implements and maintains appropriate technical and organisational measures to
                protect Personal Data against accidental or unlawful destruction, loss, alteration, unauthorised
                disclosure, or access. These measures include:
              </p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>Encryption of Personal Data in transit using TLS and at rest</li>
                <li>Role-based access controls limiting access to Personal Data to authorised personnel only</li>
                <li>Regular security assessments and infrastructure monitoring</li>
                <li>Secure authentication mechanisms including email verification</li>
                <li>Logical data isolation between customer accounts</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">6. Sub-processors</h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>
                The Controller hereby provides general authorisation for SiteProof Ltd to engage the following
                Sub-processors in connection with the provision of the SiteProof platform:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-4 font-semibold text-gray-700">Sub-processor</th>
                      <th className="text-left py-2 pr-4 font-semibold text-gray-700">Purpose</th>
                      <th className="text-left py-2 font-semibold text-gray-700">Location</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr>
                      <td className="py-2 pr-4 text-gray-600">Supabase</td>
                      <td className="py-2 pr-4 text-gray-600">Database and authentication</td>
                      <td className="py-2 text-gray-600">EU / UK</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 text-gray-600">Stripe</td>
                      <td className="py-2 pr-4 text-gray-600">Payment processing</td>
                      <td className="py-2 text-gray-600">USA (SCCs in place)</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 text-gray-600">Amazon Web Services</td>
                      <td className="py-2 pr-4 text-gray-600">Cloud infrastructure and AI features</td>
                      <td className="py-2 text-gray-600">EU / UK</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 text-gray-600">Resend</td>
                      <td className="py-2 pr-4 text-gray-600">Transactional email delivery</td>
                      <td className="py-2 text-gray-600">USA (SCCs in place)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p>
                SiteProof Ltd will notify the Controller of any intended changes to Sub-processors, providing
                the Controller with the opportunity to object to such changes.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">7. Data Retention and Deletion</h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>
                Upon termination of the SiteProof subscription, SiteProof Ltd will:
              </p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>Retain Personal Data for 30 days to allow the Controller to export data</li>
                <li>Permanently delete Personal Data from live systems after 30 days</li>
                <li>Overwrite backup copies within 90 days of deletion from live systems</li>
                <li>Retain financial records for 7 years as required by UK law</li>
              </ul>
              <p>
                The Controller may request deletion of Personal Data at any time by contacting{' '}
                <a href="mailto:support@site-proof.app" className="text-emerald-600 hover:text-emerald-700 underline">
                  support@site-proof.app
                </a>.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">8. Data Subject Rights</h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>
                SiteProof Ltd will assist the Controller in fulfilling its obligations to respond to Data
                Subject requests under UK GDPR, including requests for access, rectification, erasure,
                restriction, portability, and objection.
              </p>
              <p>
                Where a Data Subject contacts SiteProof Ltd directly with a request, SiteProof Ltd will
                promptly notify the Controller and await instructions before taking any action.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">9. Data Breach Notification</h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>
                In the event of a Personal Data breach, SiteProof Ltd will notify the Controller without undue
                delay and, where feasible, within 72 hours of becoming aware of the breach. The notification
                will include:
              </p>
              <ul className="list-disc list-inside space-y-1 pl-2">
                <li>A description of the nature of the breach</li>
                <li>The categories and approximate number of Data Subjects and records affected</li>
                <li>The likely consequences of the breach</li>
                <li>Measures taken or proposed to address the breach</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">10. International Transfers</h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>
                Where Personal Data is transferred outside the UK or EEA, SiteProof Ltd ensures that
                appropriate safeguards are in place, including Standard Contractual Clauses (SCCs) or
                adequacy decisions, in accordance with UK GDPR requirements.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">11. Governing Law</h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>
                This DPA is governed by the laws of England and Wales. Any disputes arising under this DPA
                shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">12. Contact</h2>
            <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
              <p>For any queries relating to this DPA, please contact:</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-3">
                <p className="font-medium text-gray-900">SiteProof Ltd</p>
                <p>Registered in England and Wales</p>
                <p>Company number: 17085500</p>
                <p>
                  Email:{' '}
                  <a href="mailto:support@site-proof.app" className="text-emerald-600 hover:text-emerald-700">
                    support@site-proof.app
                  </a>
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <SiteProofLogo size="sm" />
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <Link to="/terms-of-service" className="text-xs text-gray-500 hover:text-gray-900 transition-colors">
                Terms of Service
              </Link>
              <Link to="/privacy-policy" className="text-xs text-gray-500 hover:text-gray-900 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookie-policy" className="text-xs text-gray-500 hover:text-gray-900 transition-colors">
                Cookie Policy
              </Link>
              <Link to="/" className="text-xs text-gray-500 hover:text-gray-900 transition-colors">
                Back to SiteProof
              </Link>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-xs text-gray-400">&copy; 2026 SiteProof Ltd. All rights reserved.</p>
              <p className="text-xs text-gray-300 mt-0.5">SiteProof Ltd is registered in England and Wales. Company number: 17085500.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DataProcessingAgreement;
